const initialState = {
  conversations: {
    data: [],
    loading: false,
    error: null,
  },
  messages: {
    // Structured by conversation ID
    // e.g. "1": { data: [], loading: false, error: null, pagination: {} }
  },
  unreadCount: 0,
  pendingActions: {
    sendMessage: {
      inProgress: false,
      error: null,
    },
    deleteMessage: {
      inProgress: false,
      error: null,
    },
    reportMessage: {
      inProgress: false,
      error: null,
    },
    ensureConversation: {
      inProgress: false,
      error: null,
    },
  },
};

export default function messaging(state = initialState, action) {
  switch (action.type) {
    // Conversations actions
    case 'CONVERSATIONS_LOADING':
      return {
        ...state,
        conversations: {
          ...state.conversations,
          loading: true,
          error: null,
        },
      };
    case 'CONVERSATIONS_LOADED':
      return {
        ...state,
        conversations: {
          data: action.payload.data,
          loading: false,
          error: null,
        },
      };
    case 'CONVERSATIONS_ERROR':
      return {
        ...state,
        conversations: {
          ...state.conversations,
          loading: false,
          error: action.payload,
        },
      };

    case 'ENSURE_CONVERSATION_REQUEST':
      return {
        ...state,
        pendingActions: {
          ...state.pendingActions,
          ensureConversation: {
            inProgress: true,
            error: null,
          },
        },
      };
    case 'ENSURE_CONVERSATION_SUCCESS': {
      const conversation = action.payload.conversation;
      const conversationsState = state.conversations || {};
      const conversationsData = conversationsState.data || [];
      const existingIndex = conversationsData.findIndex(
        existingConversation => existingConversation && existingConversation.id === conversation.id,
      );

      let updatedData;
      let nextUnreadCount = state.unreadCount;

      const nextUnread =
        typeof conversation.unreadCount === 'number' ? conversation.unreadCount : 0;

      if (existingIndex !== -1) {
        const previousConversation = conversationsData[existingIndex] || {};
        const previousUnread =
          previousConversation && typeof previousConversation.unreadCount === 'number'
            ? previousConversation.unreadCount
            : 0;
        const unreadDelta = nextUnread - previousUnread;
        nextUnreadCount = Math.max(0, nextUnreadCount + unreadDelta);

        updatedData = conversationsData.map(existingConversation => {
          if (existingConversation && existingConversation.id === conversation.id) {
            return { ...existingConversation, ...conversation };
          }

          return existingConversation;
        });
      } else {
        nextUnreadCount = Math.max(0, nextUnreadCount + nextUnread);
        updatedData = [conversation, ...conversationsData];
      }

      return {
        ...state,
        conversations: {
          ...conversationsState,
          data: updatedData,
        },
        pendingActions: {
          ...state.pendingActions,
          ensureConversation: {
            inProgress: false,
            error: null,
          },
        },
        unreadCount: nextUnreadCount,
      };
    }
    case 'ENSURE_CONVERSATION_ERROR':
      return {
        ...state,
        pendingActions: {
          ...state.pendingActions,
          ensureConversation: {
            inProgress: false,
            error: action.payload ? action.payload.error : null,
          },
        },
      };

    // Messages actions
    case 'MESSAGES_LOADING':
      return {
        ...state,
        messages: {
          ...state.messages,
          [action.payload.conversationId]: {
            ...(state.messages[action.payload.conversationId] || {}),
            loading: true,
            error: null,
          },
        },
      };
    case 'MESSAGES_LOADED':
      return {
        ...state,
        messages: {
          ...state.messages,
          [action.payload.conversationId]: {
            data: action.payload.data.data,
            pagination: action.payload.data.pagination,
            loading: false,
            error: null,
          },
        },
      };
    case 'MESSAGES_ERROR':
      return {
        ...state,
        messages: {
          ...state.messages,
          [action.payload.conversationId]: {
            ...(state.messages[action.payload.conversationId] || {}),
            loading: false,
            error: action.payload.error,
          },
        },
      };

    // Send message actions
    case 'SEND_MESSAGE_PENDING': {
      const conversationId = action.payload.conversationId;
      const conversationState = state.messages[conversationId] || {};
      const currentMessages = conversationState.data || [];

      return {
        ...state,
        pendingActions: {
          ...state.pendingActions,
          sendMessage: {
            inProgress: true,
            error: null,
          },
        },
        messages: {
          ...state.messages,
          [conversationId]: {
            ...conversationState,
            // Add the optimistic message to the end of the array
            data: [...currentMessages, action.payload.optimisticMessage],
          },
        },
      };
    }
    case 'SEND_MESSAGE_SUCCESS': {
      // Get current messages for this conversation
      const conversationId = action.payload.conversationId;
      const conversationState = state.messages[conversationId] || {};
      const currentMessages = conversationState.data || [];

      // Replace the optimistic message with the real one
      const updatedMessages = currentMessages.map(msg => {
        if (msg.id === action.payload.tempId) {
          return action.payload.message;
        }
        return msg;
      });

      return {
        ...state,
        pendingActions: {
          ...state.pendingActions,
          sendMessage: {
            inProgress: false,
            error: null,
          },
        },
        messages: {
          ...state.messages,
          [conversationId]: {
            ...conversationState,
            data: updatedMessages,
          },
        },
      };
    }
    case 'SEND_MESSAGE_ERROR': {
      const conversationId = action.payload.conversationId;
      const conversationState = state.messages[conversationId] || {};
      const currentMessages = conversationState.data || [];

      // Remove the failed optimistic message
      const filteredMessages = currentMessages.filter(msg => msg.id !== action.payload.tempId);

      return {
        ...state,
        pendingActions: {
          ...state.pendingActions,
          sendMessage: {
            inProgress: false,
            error: action.payload.error,
          },
        },
        messages: {
          ...state.messages,
          [conversationId]: {
            ...conversationState,
            data: filteredMessages,
          },
        },
      };
    }

    // Delete message actions
    case 'DELETE_MESSAGE_PENDING':
      return {
        ...state,
        pendingActions: {
          ...state.pendingActions,
          deleteMessage: {
            inProgress: true,
            error: null,
          },
        },
      };
    case 'DELETE_MESSAGE_SUCCESS': {
      // Create a new state with the message removed from all conversations
      const messageId = action.payload.messageId;
      const newMessages = { ...state.messages };

      // Remove message from any conversation where it exists
      Object.keys(newMessages).forEach(conversationId => {
        const conversation = newMessages[conversationId] || {};
        if (conversation.data) {
          newMessages[conversationId] = {
            ...conversation,
            data: conversation.data.filter(message => message.id !== messageId),
          };
        }
      });

      return {
        ...state,
        pendingActions: {
          ...state.pendingActions,
          deleteMessage: {
            inProgress: false,
            error: null,
          },
        },
        messages: newMessages,
      };
    }
    case 'DELETE_MESSAGE_ERROR':
      return {
        ...state,
        pendingActions: {
          ...state.pendingActions,
          deleteMessage: {
            inProgress: false,
            error: action.payload.error,
          },
        },
      };

    // Report message actions
    case 'REPORT_MESSAGE_PENDING':
      return {
        ...state,
        pendingActions: {
          ...state.pendingActions,
          reportMessage: {
            inProgress: true,
            error: null,
          },
        },
      };
    case 'REPORT_MESSAGE_SUCCESS':
      return {
        ...state,
        pendingActions: {
          ...state.pendingActions,
          reportMessage: {
            inProgress: false,
            error: null,
          },
        },
      };
    case 'REPORT_MESSAGE_ERROR':
      return {
        ...state,
        pendingActions: {
          ...state.pendingActions,
          reportMessage: {
            inProgress: false,
            error: action.payload.error,
          },
        },
      };

    // Update state directly
    case 'UPDATE_MESSAGING_STATE':
      return {
        ...state,
        ...action.payload,
      };

    // Set unread message count
    case 'SET_UNREAD_MESSAGE_COUNT':
      return {
        ...state,
        unreadCount: action.payload,
      };

    case 'MARK_CONVERSATION_READ': {
      const { conversationId, unreadDelta } = action.payload || {};
      const numericConversationId = Number(conversationId);
      const conversationsState = state.conversations || {};
      const conversationsData = conversationsState.data || [];

      let delta = typeof unreadDelta === 'number' ? unreadDelta : 0;

      const updatedConversationsData = conversationsData.map(conversation => {
        if (!conversation || conversation.id !== numericConversationId) {
          return conversation;
        }

        if (typeof unreadDelta !== 'number') {
          delta = typeof conversation.unreadCount === 'number' ? conversation.unreadCount : 0;
        }

        return {
          ...conversation,
          unreadCount: 0,
        };
      });

      const nextUnreadCount = Math.max(0, state.unreadCount - delta);

      return {
        ...state,
        conversations: {
          ...conversationsState,
          data: updatedConversationsData,
        },
        unreadCount: nextUnreadCount,
      };
    }

    default:
      return state;
  }
}



// WEBPACK FOOTER //
// ./src/reducers/messaging.js